import { useCallback, useEffect, useState } from 'react';

import { createNexusApi } from '../../domain/nexus/api';
import type { GoogleNexusUpload } from '../../domain/nexus/model';
import { firebaseNexusGateway } from '../../platform/firebase/nexusGateway';
import { logGlobalError } from '../../utils/globalErrorLogger';
import { diagnosticMessage } from '../AdminPage/tools/GoogleNexusTool/helpers';
import { applyRefreshResult, shouldPollUpload } from './polling';

const nexusApi = createNexusApi(firebaseNexusGateway);

export const NEXUS_UPLOAD_POLL_INTERVAL_MS = 5_000;

export interface AdminNexusUploadDetailViewModel {
  busy: boolean;
  error: string | null;
  pollError: string | null;
  upload: GoogleNexusUpload | null;
  polling: boolean;
  initialize: () => Promise<void>;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return diagnosticMessage(error);
}

export function useAdminNexusUploadDetailViewModel(
  uploadId: string,
): AdminNexusUploadDetailViewModel {
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [upload, setUpload] = useState<GoogleNexusUpload | null>(null);

  const initialize = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { upload: found } = await nexusApi.fetchUpload(uploadId);
      setUpload(found);
      if (!found) setError('Google Nexus upload not found.');
    } catch (nextError) {
      setError(errorMessage(nextError));
      logGlobalError(nextError, {
        context: 'AdminNexusUploadDetailPage.initialize',
        metadata: { uploadId },
      });
    } finally {
      setBusy(false);
    }
  }, [uploadId]);

  const polling = shouldPollUpload(upload);

  // Poll Nexus while the validation is active. The cleanup runs on unmount
  // (navigation away) and when the upload reaches a terminal status.
  useEffect(() => {
    if (!polling) return undefined;
    let cancelled = false;
    let timer: number | undefined;
    const tick = async () => {
      try {
        const result = await nexusApi.refreshUpload(uploadId);
        if (cancelled) return;
        const polledAt = new Date().toISOString();
        setUpload((current) => (current ? applyRefreshResult(current, result, polledAt) : current));
        setPollError(result.ok ? null : diagnosticMessage(result.error));
      } catch (nextError) {
        // Best-effort; the next tick retries.
        if (!cancelled) setPollError(errorMessage(nextError));
      }
      if (!cancelled) timer = window.setTimeout(() => void tick(), NEXUS_UPLOAD_POLL_INTERVAL_MS);
    };
    void tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [polling, uploadId]);

  return { busy, error, pollError, upload, polling, initialize };
}
